// ASPIRE offline storage — implements HANDOFF schema Section 6 exactly.
//
// Database: "aspire_offline_v1", version 1
//   pending_uploads   — full session records (manifest + audio blobs) awaiting upload
//   participant_state — single record, id="state" — the whole app state machine's memory
//   content_cache     — cached texts/images fetched from /content-manifest
//   session_history   — lightweight per-session ledger for the dashboard/upload retry UI

import { openDB } from 'idb';

const DB_NAME = 'aspire_offline_v1';
const DB_VERSION = 1;

let dbPromise = null;

export function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('pending_uploads')) {
          const store = db.createObjectStore('pending_uploads', { keyPath: 'session_id' });
          store.createIndex('status', 'status');
        }
        if (!db.objectStoreNames.contains('participant_state')) {
          db.createObjectStore('participant_state', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('content_cache')) {
          db.createObjectStore('content_cache', { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains('session_history')) {
          const store = db.createObjectStore('session_history', { keyPath: 'session_id' });
          store.createIndex('session_number', 'session_number');
        }
      },
    });
  }
  return dbPromise;
}

// ---------- participant_state (singleton record) ----------

export const DEFAULT_STATE = {
  id: 'state',
  participant_id: null,
  session_number: 0,
  content_index: 0,
  active_in_session: false,
  current_session_id: null,
  study_complete: false,
  schedule: null,
  notifications_enabled: false,
  fcm_token: null,
  last_session_ts: null,
  pwa_install_date: null,
  researcher_token: null,
  researcher_token_expires: null,
};

export async function getParticipantState() {
  const db = await getDB();
  const state = await db.get('participant_state', 'state');
  return state ?? { ...DEFAULT_STATE };
}

export async function putParticipantState(state) {
  const db = await getDB();
  await db.put('participant_state', { ...state, id: 'state' });
  return state;
}

export async function patchParticipantState(patch) {
  const current = await getParticipantState();
  const next = { ...current, ...patch };
  await putParticipantState(next);
  return next;
}

// ---------- pending_uploads ----------

export async function addPendingUpload(record) {
  const db = await getDB();
  await db.put('pending_uploads', record);
}

export async function updatePendingUpload(sessionId, patch) {
  const db = await getDB();
  const existing = await db.get('pending_uploads', sessionId);
  if (!existing) return null;
  const next = { ...existing, ...patch };
  await db.put('pending_uploads', next);
  return next;
}

export async function getPendingUpload(sessionId) {
  const db = await getDB();
  return db.get('pending_uploads', sessionId);
}

export async function getUploadsByStatus(statuses) {
  const db = await getDB();
  const all = await db.getAll('pending_uploads');
  return all.filter((r) => statuses.includes(r.status));
}

export async function deletePendingUpload(sessionId) {
  const db = await getDB();
  await db.delete('pending_uploads', sessionId);
}

export async function countPendingUploads() {
  const db = await getDB();
  const all = await db.getAllFromIndex('pending_uploads', 'status');
  return all.filter((r) => r.status === 'pending' || r.status === 'failed').length;
}

// ---------- content_cache ----------

export async function putContentCache(key, type, data) {
  const db = await getDB();
  await db.put('content_cache', { key, type, data, cached_at: new Date().toISOString() });
}

export async function getContentCache(key) {
  const db = await getDB();
  return db.get('content_cache', key);
}

export async function getAllContentCache() {
  const db = await getDB();
  return db.getAll('content_cache');
}

export async function clearContentCache() {
  const db = await getDB();
  await db.clear('content_cache');
}

// ---------- session_history ----------

export async function addSessionHistory(record) {
  const db = await getDB();
  await db.put('session_history', record);
}

export async function updateSessionHistory(sessionId, patch) {
  const db = await getDB();
  const existing = await db.get('session_history', sessionId);
  const next = { ...(existing ?? { session_id: sessionId }), ...patch };
  await db.put('session_history', next);
  return next;
}

export async function getAllSessionHistory() {
  const db = await getDB();
  return db.getAll('session_history');
}

// ---------- storage quota (Section 13) ----------

export async function checkStorageQuota() {
  if (!navigator.storage?.estimate) return { usage: 0, quota: 0, ratio: 0 };
  const { usage = 0, quota = 0 } = await navigator.storage.estimate();
  return { usage, quota, ratio: quota > 0 ? usage / quota : 0 };
}

export async function requestPersistentStorage() {
  if (!navigator.storage?.persist) return false;
  try {
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
